import { useRole } from '@/hooks/useRole'
import { useServiceActions } from '@/hooks/useServiceActions'
import { Service, SharedProps } from '@/types'
import { usePage } from '@inertiajs/react'
import { Badge, Box, Button, Group, Paper, Stack, Text } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import dayjs from 'dayjs'

interface ServiceCardProps {
  service: Service
}

export function ServiceCard({ service }: ServiceCardProps) {
  const {
    id,
    service_name,
    instructions,
    verified_at,
    completed_at,
    completed_by_name,
    verified_by_name,
    article_id,
    order_id,
  } = service
  const { props } = usePage<SharedProps>()
  const { isAccueil, isAdmin, isAtelierOrg } = useRole()
  const { current_organization } = props

  const canVerify = isAtelierOrg && (isAccueil || isAdmin)

  const { completeService, verifyService } = useServiceActions(current_organization?.slug || '', order_id)

  return (
    <Paper withBorder p="md" radius="md" shadow="xs">
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Box>
            <Text fw={800} size="lg">
              {service_name}
            </Text>
            {instructions && (
              <Text size="sm" c="dimmed">
                {instructions}
              </Text>
            )}
          </Box>
          <Badge
            color={verified_at ? 'green' : completed_at ? 'blue' : 'gray'}
            variant="light"
            size="lg"
            leftSection={verified_at && <IconCheck size={14} />}
          >
            {verified_at ? 'Vérifié' : completed_at ? 'Terminé' : 'À faire'}
          </Badge>
        </Group>

        <Stack gap="sm">
          {completed_at && (
            <Group gap={4} c="dimmed">
              {completed_by_name && <Text size="xs">Terminé par {completed_by_name}</Text>}
              <Text size="xs">Le {dayjs(completed_at).format('DD/MM/YYYY HH:mm')}</Text>
            </Group>
          )}

          {verified_at && (
            <Group gap={4} c="dimmed">
              {verified_by_name && <Text size="xs">Vérifié par {verified_by_name}</Text>}
              <Text size="xs">Le {dayjs(verified_at).format('DD/MM/YYYY HH:mm')}</Text>
            </Group>
          )}

          {isAtelierOrg && !verified_at && (
            <Button
              fullWidth
              variant={completed_at ? 'light' : 'filled'}
              color={completed_at ? 'gray' : 'blue'}
              onClick={() => completeService(article_id, id)}
            >
              {completed_at ? 'Annuler la terminaison' : 'Terminer le service'}
            </Button>
          )}

          {canVerify && (
            <Button
              fullWidth
              variant={verified_at ? 'light' : 'filled'}
              color={verified_at ? 'gray' : 'green'}
              disabled={!completed_at && !verified_at}
              onClick={() => verifyService(article_id, id)}
            >
              {verified_at ? 'Annuler la vérification' : 'Marquer comme vérifié'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}
